import { useState } from 'react';
import styled from 'styled-components';
import type { Role } from '@frameio/graphql-client/generated';
import {
  Button,
  Menu,
  MenuButton,
  MenuGroup,
  MenuRadio,
  MenuRadioGroup,
  Text,
  theme,
  whenHovered,
} from '@frameio/vapor';
import { IconChevronDown } from '@frameio/vapor-icons';
import { withBackgroundTransition } from '../styles';
import type { InviteAccessMemberProps } from '../types';
import TEXT from './generated/TEXT/InviteAccessMemberRoleSelect';

TEXT.DEFINE({
  /** Aria label for the default role select */
  labelDefault: 'Change role',
  /** Aria label for the role select */
  labelRole: (roleName: string) => `Current role: ${roleName}`,
  /** Text for the remove member button */
  removeMember: 'Remove access',
});

type InviteAccessMemberRoleSelectProps = {
  label?: string;
  onRemove?: () => void;
  onUpdateRole?: (roleId: Role['id']) => void;
  roleId?: Role['id'];
  roleName: Role['name'];
} & Pick<InviteAccessMemberProps, 'availableRoles' | 'accessCanBeManaged'>;

export const InviteAccessMemberRoleSelect = ({
  availableRoles,
  accessCanBeManaged,
  label = TEXT.labelDefault,
  onRemove,
  onUpdateRole,
  roleId,
  roleName,
}: InviteAccessMemberRoleSelectProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const canUpdateRole = accessCanBeManaged && onRemove && Boolean(onUpdateRole);
  const currentRoleIsAssignable =
    availableRoles?.some((role) =>
      roleId ? role.id === roleId : roleName === role.name
    ) ?? true;

  return canUpdateRole ? (
    <Menu
      content={
        <>
          <MenuRadioGroup
            onChange={(value) => {
              onUpdateRole?.(value);
            }}
          >
            {availableRoles?.map((role) => (
              <MenuRadio
                key={role.id}
                checked={roleId ? role.id === roleId : roleName === role.name}
                details={role.description}
                name={role.id}
              >
                {role.name}
              </MenuRadio>
            ))}
          </MenuRadioGroup>

          <MenuGroup>
            <MenuButton onClick={onRemove} variant="destructive">
              {TEXT.removeMember}
            </MenuButton>
          </MenuGroup>
        </>
      }
      isShowing={isMenuOpen}
      onRequestChange={setIsMenuOpen}
      placement="bottom-end"
      shouldUsePortal={true}
    >
      <StyledButton
        aria-label={`${label}: ${TEXT.labelRole(roleName)}`}
        disabled={!accessCanBeManaged || !currentRoleIsAssignable}
        iconAfter={
          <IconChevronDown color="var(--colors-icon-secondary)" size={16} />
        }
        size="small"
      >
        {roleName}
      </StyledButton>
    </Menu>
  ) : (
    <StyledDiv>
      <Text color="tertiary" variant="body">
        {roleName}
      </Text>
    </StyledDiv>
  );
};

const StyledButton = styled(Button)`
  ${withBackgroundTransition}
  background-color: ${theme.colors.components.select.background};
  color: ${theme.colors.text.primary};
  justify-content: space-between;
  min-width: 142px;

  ${(p) =>
    p.disabled &&
    `
    cursor: not-allowed;
    pointer-events: auto;

    ${whenHovered} {
      background-color: ${theme.colors.background.interactive.disabled};
      color: ${theme.colors.text.interactive.disabled};
    }
  `}
`;

const StyledDiv = styled.div`
  flex-shrink: 0;
  min-width: 70px;
  padding-right: ${theme.spaces[6]};
  text-align: right;
`;
